import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Link2, Loader2, AlertTriangle } from "lucide-react";

export function PcoConnect() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ connected: false });
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations/planning-center/status");
        setStatus(data || { connected: false });
      } catch {
        setStatus({ connected: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const connect = async () => {
    setStarting(true);
    try {
      const { data } = await api.get("/integrations/planning-center/start");
      if (data?.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        toast.error("Could not start Planning Center connection");
        setStarting(false);
      }
    } catch {
      toast.error("Could not start Planning Center connection");
      setStarting(false);
    }
  };

  return (
    <Card className="p-6 max-w-2xl" data-testid="pco-connect-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-semibold text-[#2A2626] flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Planning Center
          </h3>
          <p className="text-sm text-[#5C5856] mt-1 max-w-md">
            Connect your church's Planning Center account so Connect Card responses can create and update people in your church database.
          </p>
        </div>

        {loading ? (
          <span className="text-sm text-[#5C5856] inline-flex items-center gap-1.5" data-testid="pco-connect-status">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking…
          </span>
        ) : status.connected && status.healthy ? (
          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-[#E7EFE6] text-[#4F6A4C] inline-flex items-center gap-1.5" data-testid="pco-connect-status">
            <CheckCircle2 className="w-3.5 h-3.5" /> Connected
          </span>
        ) : status.connected ? (
          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-[#FBF0DA] text-[#8A6D2F] inline-flex items-center gap-1.5" data-testid="pco-connect-status">
            <AlertTriangle className="w-3.5 h-3.5" /> Needs attention
          </span>
        ) : (
          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-[#F0EBE1] text-[#5C5856]" data-testid="pco-connect-status">
            Not connected
          </span>
        )}
      </div>

      {!loading && status.connected && status.healthy && (
        <div className="mt-4 rounded-lg bg-[#F7F5F1] border border-[#E5E2DC] p-4">
          <p className="text-sm text-[#2A2626]">
            Connected to{" "}
            <span className="font-semibold" data-testid="pco-connect-org-name">
              {status.organization_name || "your Planning Center organization"}
            </span>
          </p>
          {status.pco_organization_id && (
            <p className="text-[11px] text-[#8A857F] mt-1" data-testid="pco-connect-org-id">
              Organization ID: {status.pco_organization_id}
            </p>
          )}
        </div>
      )}

      {!loading && status.connected && !status.healthy && (
        <div className="mt-4 rounded-lg bg-[#FCF6E9] border border-[#EAD9AE] p-4" data-testid="pco-connect-warning">
          <p className="text-sm font-medium text-[#8A6D2F] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Planning Center connection needs attention
          </p>
          <p className="text-sm text-[#7A6A44] mt-1">
            We can't reach your Planning Center account right now. Reconnect to keep sending Connect Card responses to your church database.
          </p>
        </div>
      )}

      <div className="mt-5">
        {!loading && status.connected && status.healthy ? (
          <Button
            onClick={connect}
            disabled={starting}
            variant="outline"
            className="rounded-full"
            data-testid="pco-reconnect-button"
          >
            {starting ? "Redirecting…" : "Change connection"}
          </Button>
        ) : !loading && status.connected && !status.healthy ? (
          <Button
            onClick={connect}
            disabled={starting}
            className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white"
            data-testid="pco-reconnect-button"
          >
            {starting ? "Redirecting…" : "Reconnect Planning Center"}
          </Button>
        ) : (
          <Button
            onClick={connect}
            disabled={loading || starting}
            className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white"
            data-testid="pco-connect-button"
          >
            {starting ? "Redirecting…" : "Connect Planning Center"}
          </Button>
        )}
      </div>
    </Card>
  );
}
